/**
 * Utility de serialização defensiva de erros para logging.
 *
 * Resolve o problema crônico de aparecer "[object Object]" no HyperDX/console
 * quando alguém faz {@code console.error('Falha:', err)} com um HttpErrorResponse,
 * TimeoutError do RxJS, ou qualquer instância de Error. JSON.stringify nesses
 * objetos descarta props non-enumerable (status, message de Error, etc), e o
 * default toString do JS retorna "[object Object]".
 *
 * Esta função extrai TODOS os campos relevantes de qualquer formato comum de
 * erro em fluxos HTTP do Angular, e retorna um objeto plano que serializa
 * corretamente em qualquer logger (console, HyperDX, Sentry, etc).
 *
 * IMPORTANTE: nunca tenta logar headers, body de request, ou qualquer payload
 * que possa conter PII / token / segredo. Apenas metadados de resposta + texto
 * de erro retornado pelo servidor (que já é seguro).
 *
 * Uso típico:
 * <pre>
 *   .catch(err => {
 *     console.error('[QR-RESOLVE] Falha', unwrapError(err));
 *     // saída: { name: 'HttpErrorResponse', status: 404, statusText: 'Not Found',
 *     //         message: 'Http failure response for /qr/public/...: 404 Not Found',
 *     //         serverMessage: 'QR não encontrado ou inválido.', url: '...' }
 *   });
 * </pre>
 */
export interface UnwrappedError {
  name: string;
  message: string;
  status?: number;
  statusText?: string;
  url?: string;
  /** Mensagem retornada pelo backend no body (ex: { message: '...' }). */
  serverMessage?: string;
  /** Body raw do erro quando não é JSON estruturado (string ou primitivo). */
  errorBody?: string;
  /** Para TimeoutError do RxJS e similares. */
  code?: string;
}

/**
 * Extrai metadados de qualquer erro JS/Angular pra logging seguro.
 *
 * @param err valor capturado em catch — qualquer coisa
 * @returns objeto plano com os campos disponíveis
 */
export function unwrapError(err: unknown): UnwrappedError {
  if (err == null) {
    return { name: 'NullError', message: 'erro nulo ou undefined' };
  }

  // Tipos primitivos: string, number, boolean
  if (typeof err === 'string') {
    return { name: 'String', message: err };
  }
  if (typeof err === 'number' || typeof err === 'boolean') {
    return { name: typeof err, message: String(err) };
  }

  const anyErr = err as any;

  const out: UnwrappedError = {
    name: anyErr?.name ?? anyErr?.constructor?.name ?? 'Unknown',
    message: anyErr?.message ?? 'sem mensagem',
  };

  // HTTP — Angular HttpErrorResponse e similares
  if (typeof anyErr?.status === 'number') {
    out.status = anyErr.status;
  }
  if (typeof anyErr?.statusText === 'string') {
    out.statusText = anyErr.statusText;
  }
  if (typeof anyErr?.url === 'string') {
    out.url = anyErr.url;
  }

  // Body de erro — pode ser objeto JSON ({message: '...'}) ou string
  const body = anyErr?.error;
  if (body != null) {
    if (typeof body === 'string') {
      // Servidor retornou string crua (raro). Limita tamanho pra não floodar log.
      out.errorBody = body.length > 500 ? body.substring(0, 500) + '...' : body;
    } else if (typeof body === 'object') {
      const serverMsg = body?.message ?? body?.error ?? body?.detail;
      if (typeof serverMsg === 'string') {
        out.serverMessage = serverMsg;
      }
    }
  }

  // RxJS TimeoutError, AbortError, custom code
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code;
  }

  return out;
}

/**
 * Retorna a melhor mensagem disponível no erro pra exibir ao usuário.
 *
 * Prioriza: serverMessage (mensagem do backend) > message (do JS error).
 * Se nada for útil, retorna fallback genérico.
 */
export function userFriendlyMessage(err: unknown, fallback: string): string {
  const u = unwrapError(err);
  return u.serverMessage || u.message || fallback;
}
